import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useCreateSkill } from '../../hooks/useSkills';

interface SkillImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const EXAMPLE = `---
name: my-skill
description: A brief description of what this skill does
tags:
  - workflow
---

## Steps
1. First step
2. Second step

## Rules
- Keep it concise
`;

export const SkillImportDialog: React.FC<SkillImportDialogProps> = ({ isOpen, onClose }) => {
  const { darkMode } = useTheme();
  const [content, setContent] = useState('');
  const [notes, setNotes] = useState('');
  const [showExample, setShowExample] = useState(false);
  const createMutation = useCreateSkill();

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!content.trim()) return;
    try {
      await createMutation.mutateAsync({ content, notes: notes || undefined });
      setContent('');
      setNotes('');
      onClose();
    } catch (e: any) {
      // error shown via mutation state
    }
  };

  const handleClose = () => {
    setContent('');
    setNotes('');
    createMutation.reset();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`w-full max-w-2xl mx-4 rounded-lg shadow-2xl border flex flex-col ${
          darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
        }`}
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className={`p-4 border-b flex items-center justify-between flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h3 className="text-lg font-semibold">Submit a Skill</h3>
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Paste markdown content — submitted skills go to the review queue
            </p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-200">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
          {/* Example toggle */}
          <div className="flex items-center justify-between">
            <label className={`text-xs font-medium uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Markdown Content *
            </label>
            <button
              onClick={() => {
                setShowExample(!showExample);
                if (!showExample && !content) setContent(EXAMPLE);
              }}
              className={`text-xs ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
            >
              {showExample ? 'Hide example' : 'Load example'}
            </button>
          </div>

          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={16}
            placeholder={`---\nname: my-skill\ndescription: What this skill does\n---\n\n## Steps\n1. ...`}
            className={`w-full px-3 py-2 rounded-md border text-sm font-mono resize-y ${
              darkMode
                ? 'bg-gray-900 border-gray-600 text-gray-200 placeholder-gray-600'
                : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />

          <div>
            <label className={`block text-xs font-medium uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Notes (optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why are you submitting this skill?"
              className={`w-full px-3 py-2 rounded-md border text-sm ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Validation errors */}
          {createMutation.isError && (
            <div className={`text-sm px-3 py-2 rounded-md ${darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {(createMutation.error as any)?.response?.data?.detail ?? 'Submission failed. Check your frontmatter.'}
            </div>
          )}

          {createMutation.isSuccess && (
            <div className={`text-sm px-3 py-2 rounded-md ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-700 border border-green-200'}`}>
              Skill submitted! It will appear in the review queue for admin approval.
            </div>
          )}

          {/* Requirements hint */}
          <div className={`text-xs rounded-md p-3 ${darkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
            <p className="font-medium mb-1">Requirements:</p>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>Must start with YAML frontmatter (<code>---</code>)</li>
              <li>Frontmatter must include <code>name</code> (slug) and <code>description</code></li>
              <li>Name must be lowercase alphanumeric with hyphens/underscores</li>
              <li>Max 50KB, ~8000 tokens</li>
              <li>No executable scripts, iframes, or dangerous HTML</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className={`p-4 border-t flex justify-end gap-2 flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={handleClose}
            className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || createMutation.isLoading}
            className={`px-4 py-2 rounded-md text-sm text-white transition-colors ${
              !content.trim() || createMutation.isLoading
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {createMutation.isLoading ? 'Submitting...' : 'Submit for Review'}
          </button>
        </div>
      </div>
    </div>
  );
};
